import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import api from "services/api";
import { usePageTitle } from "hooks/usePageTitle";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
  Stack,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import {
  DataGrid,
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import ExcelJS from "exceljs";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  getDateProximityColor,
  getProjectNumber,
  getStatusColor,
  numericComparator,
} from "helpers/ServiceHelpers";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CONST } from "constants/CONST";
import CEQuickFilter from "components/CEQuickFilter";
import { HtmlTooltip } from "components/HeatMap";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import { NAVBAR_HEIGHT } from "constants/layout";
const Interpretations = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault("America/New_York");

  usePageTitle("Interpretations");
  const theme = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const [isLoading, setIsLoading] = useState(true);

  // Grid management state
  const getSavedDensity = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_GRID_DENSITY");
    return saved || "standard";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_COLUMNS_VISIBLE");
    const parsed = saved ? JSON.parse(saved) : {};

    // Keep core cost columns visible to avoid stale hidden preferences
    // masking newly available backend fields.
    return {
      ...parsed,
      LinguistPay: true,
      TotalLinguistPay: true,
      AdditionalCost: true,
      MileageCost: true,
      ParkingFeesTolls: true,
      Address: true,
    };
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);
  const apiRef = useGridApiRef();

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("INTERPRETATIONS_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "INTERPRETATIONS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  const { setOverlayData, setOverlayName } =
    useStateContext();
  const { refreshPage, showSnackbar } = useStateContext();

  // Set up entity tracking for InterpretationsList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "InterpretationsList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "InterpretationsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const [minMaxDates, setMinMaxDates] = useState();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem(CONST.INTERPRETATION_MONTHS_KEY);
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);
  const [tempSelectedMonths, setTempSelectedMonths] = useState(getSavedMonths);

  // Year selector data
  const currentYear = dayjs().year();

  // Get saved year from localStorage or use current year as default
  const getSavedYear = () => {
    const savedYear = localStorage.getItem(CONST.INTERPRETATION_YEAR_KEY);
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  // Initialize with current year, will be updated when minMaxDates is available
  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [tempSelectedYear, setTempSelectedYear] = useState(getSavedYear);

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfClientsOPIMinimums, setListOfClientsOPIMinimums] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [isDataListsReady, setIsDataListsReady] = useState(false);
  const [pendingCreateNew, setPendingCreateNew] = useState(false);
  const [pendingPreAssign, setPendingPreAssign] = useState(false);
  const fetchInterpretationsDataRef = useRef(() => {});
  const getDataListsRef = useRef(() => {});
  const hasInitializedDataRef = useRef(false);

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]);

  const [contextMenu, setContextMenu] = useState(null);

  const [listOfServices, setListOfServices] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Derived selection for context menu actions
  const selectedInterpretation = useMemo(() => {
    return (listOfServices || []).find(({ id }) => id === selectedRow);
  }, [listOfServices, selectedRow]);

  const canCompleteSelected =
    selectedInterpretation &&
    (selectedInterpretation.InterpretationStatus === "Confirmed" ||
      selectedInterpretation.InterpretationStatus === "Requested");

  // Selection controls
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const LONG_PRESS_MS = 500;

  // Calendar sync progress overlay
  const [calendarSyncProgress, setCalendarSyncProgress] = useState({
    isOpen: false,
    current: 0,
    total: 0,
  });

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "INTERPRETATIONS_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `INTERPRETATIONS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Remove from localStorage if no values selected
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    // Get all column field names from the columns array
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "TimeStart",
      "TimeEnd",
      "Duration",
      "Details",
      "InterpretationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "TargetLanguage",
      "Profit",
      "Margin",
      "InterpretationType",
      "Address",
      "MileageRoundtrip",
      "MileageCost",
      "ParkingFeesTolls",
      "PreparationTime",
      "SchedulingTime",
      "ActualStart",
      "ActualEnd",
      "TotalDuration",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "TimeStart",
      "TimeEnd",
      "Duration",
      "Details",
      "InterpretationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "TargetLanguage",
      "Profit",
      "Margin",
      "InterpretationType",
      "Address",
      "MileageRoundtrip",
      "MileageCost",
      "ParkingFeesTolls",
      "PreparationTime",
      "SchedulingTime",
      "ActualStart",
      "ActualEnd",
      "TotalDuration",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const handleContextMenu = (event) => {
    event.preventDefault();

    const rowId = Number(event.currentTarget.getAttribute("data-id"));
    setSelectedRow(rowId);

    if (Number.isFinite(rowId)) {
      setSelectionModel((prev) => {
        if (selectionEnabled) {
          return prev.includes(rowId) ? prev : [...prev, rowId];
        }
        return [rowId];
      });
    }

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_EDIT,
          Interpretations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Interpretation",
      },
      Service: {
        id: interpretationData.id,
        ProjectNumber: interpretationData.ProjectNumber,
        Details: interpretationData.Details,
        ServiceCategory: interpretationData.ServiceCategory,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuAddNote = () => {
    const data = {
      ServiceID: selectedRow,
      MemberID: currentUser.id,
      ModelType: "Interpretation",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);

    setContextMenu(null);
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE,
          Interpretations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
          DeliveryTypes: deliveryTypes,
          PaymentTypes: paymentTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuCallRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    if (interpretationData?.Requestor?.PhoneNumber) {
      window.location.href = `tel:${interpretationData.Requestor.PhoneNumber}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuEmailRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    if (interpretationData?.Requestor?.Email) {
      const email = interpretationData.Requestor.Email;
      const subject =
        "Regarding Assignment N° " + interpretationData.ProjectNumber;
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}`;
      window.open(mailtoLink, "_blank");
    }
    setContextMenu(null);
  };

  // Helper to extract cost lines from service for duplication
  // Ensures duplicated services get proper cost-line rows, not just legacy fields
  const extractCostLinesFromService = (serviceData) => {
    if (!Array.isArray(serviceData?.CostLines) || serviceData.CostLines.length === 0) {
      return [];
    }

    return serviceData.CostLines.map((line) => ({
      CostTypeName: line.CostType?.Name || line.CostTypeName,
      SourceLegacyField: line.SourceLegacyField || null,
      Amount: Number(line.Amount || 0).toFixed(2),
      IncludeInTotalCost: line.IncludeInTotalCost ?? true,
      IncludeInAccountCharge: line.IncludeInAccountCharge ?? false,
      DisplayOrder: line.DisplayOrder || 0,
    }));
  };

  const handleContextMenuDuplicateConfirm = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        if (!interpretationData) return;

        // Calculate project number for the duplicate
        const newProjectNumber = getProjectNumber(
          interpretationData.Date,
          listOfServices,
        );

        // Extract cost lines from source to ensure duplicates get proper cost-line rows
        const duplicateCostLines = extractCostLinesFromService(interpretationData);

        const duplicatedLanguage =
          interpretationData.ServiceTargetLanguages &&
          interpretationData.ServiceTargetLanguages.length > 0
            ? interpretationData.ServiceTargetLanguages[0].Language
            : null;

        const duplicatedData = {
          Clients: listOfClients,
          ClientsOPIMinimums: listOfClientsOPIMinimums,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          Interpretations: listOfServices,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
          ProjectNumber: newProjectNumber,
          CostLines: duplicateCostLines,
          PrefilledData: {
            Date: interpretationData.Date,
            ProjectNumber: newProjectNumber,
            TimeStart: interpretationData.TimeStart || "00:00:00",
            TimeEnd: interpretationData.TimeEnd || "00:00:00",
            TotalDuration: interpretationData.TotalDuration || "00:00:00",
            Duration: interpretationData.Duration || "00:00:00",
            ActualDuration: interpretationData.ActualDuration || "00:00:00",
            Name: interpretationData.Name,
            Details: interpretationData.Details,
            InterpretationStatus: interpretationData.InterpretationStatus,
            AccountCharge: interpretationData.AccountCharge,
            TotalCost: interpretationData.TotalCost,
            AdditionalCost: interpretationData.AdditionalCost,
            AdditionalLinguistCost: interpretationData.AdditionalLinguistCost,
            LinguistPay: interpretationData.LinguistPay,
            Profit: interpretationData.Profit,
            Margin: interpretationData.Margin,
            InterpretationType: interpretationData.ServiceSubtype?.Name || "",
            Mode: interpretationData.Mode,
            Address: interpretationData.Address,
            MileageRoundtrip: interpretationData.MileageRoundtrip,
            MileageCost: interpretationData.MileageCost,
            ParkingFeesTolls: interpretationData.ParkingFeesTolls,
            PaymentFees: interpretationData.PaymentFees,
            EquipmentCost: interpretationData.EquipmentCost,
            TravelTimeCost: interpretationData.TravelTimeCost,
            TravelTimeDuration: interpretationData.TravelTimeDuration,
            PreparationTime: interpretationData.PreparationTime,
            SchedulingTime: interpretationData.SchedulingTime,
            ActualStart: interpretationData.ActualStart,
            ActualEnd: interpretationData.ActualEnd,
            Rate: interpretationData.Rate,
            RateSimultaneous: interpretationData.RateSimultaneous,
            TotalLinguistPay: interpretationData.TotalLinguistPay,
            ClientRateDiscount: interpretationData.ClientRateDiscount,
            DisableAutoChargeCalc: interpretationData.DisableAutoChargeCalc,
            IsShortAssignment: interpretationData.IsShortAssignment,
            ContactPersonName: interpretationData.ContactPersonName,
            ContactPersonNumber: interpretationData.ContactPersonNumber,
            Client: interpretationData.Client,
            Requestor: interpretationData.Requestor,
            Invoicer: interpretationData.Invoicer,
            Linguist: interpretationData.Linguist,
            Language: duplicatedLanguage,
            CostLines: duplicateCostLines,
          },
        };

        setOverlayData(duplicatedData);
        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
        setContextMenu(null);
        closeDialog();
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuDuplicate = () => {
    openDialog({
      theme: theme,
      title: "Duplicate Assignment",
      message:
        "Are you sure you want to duplicate this interpretation assignment?",
      secondaryAction: "Close",
      primaryAction: "Duplicate",
      secondaryOnClick: () => {
        closeDialog();
        setContextMenu(null);
      },
      primaryOnClick: () => {
        handleContextMenuDuplicateConfirm();
      },
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      // keep last selected for highlighting
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = () => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressNextClickRef.current = false;
  };

  const isServiceChecked = useCallback((service) => {
    return !!(service?.CheckingDate || service?.CheckedBy || service?.Checker);
  }, []);

  const isServiceEligibleForCalendar = useCallback((service) => {
    return (
      service?.ServiceCategory === "Interpretation" &&
      service?.ServiceSubtype?.Name !== "OPI"
    );
  }, []);

  const hasCalendarEvent = useCallback((service) => {
    return !!service?.graphEventId;
  }, []);

  const selectionStats = useMemo(() => {
    if (!selectionEnabled || selectionModel.length === 0) {
      return { total: 0, checked: 0, unchecked: 0, allChecked: false };
    }

    const selectedSet = new Set(selectionModel);
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedSet.has(service.id),
    );

    const checked = selectedServices.filter(isServiceChecked).length;
    const total = selectedServices.length;
    const unchecked = Math.max(total - checked, 0);

    return {
      total,
      checked,
      unchecked,
      allChecked: total > 0 && unchecked === 0,
    };
  }, [selectionEnabled, selectionModel, listOfServices, isServiceChecked]);

  const handleMarkSelectedAsChecked = () => {
    if (selectionModel.length === 0) return;

    const selectedIds = [...selectionModel];
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedIds.includes(service.id),
    );
    const uncheckedIds = selectedServices
      .filter((service) => !isServiceChecked(service))
      .map((service) => service.id);

    if (uncheckedIds.length === 0) {
      showSnackbar(
        "The selected assignment(s) are already checked.",
        theme,
        "info",
      );
      return;
    }

    const message =
      uncheckedIds.length === selectedIds.length
        ? `Mark ${selectedIds.length} assignment(s) as Checked?`
        : `Only ${uncheckedIds.length} out of ${selectedIds.length} of the selected assignments are not checked, do you want to mark them as checked?`;

    openDialog({
      theme,
      title: "Mark Assignments as Checked",
      message,
      secondaryAction: "Cancel",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          const payloads = uncheckedIds.map((id) => {
            const data = {
              id,
              CheckedBy: currentUser.id,
              CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            };
            return api.post("/services/markServiceChecked", {
              data,
            });
          });

          await Promise.all(payloads);
          showSnackbar("Assignments marked as Checked", theme, "success");
          handleCancelSelection();
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark interpretations as checked.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handleAddSelectedToCalendar = () => {
    if (selectionModel.length === 0) return;

    const selectedIds = [...selectionModel];
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedIds.includes(service.id),
    );

    // Filter for eligible services without calendar events
    const eligibleIds = selectedServices
      .filter(
        (service) =>
          isServiceEligibleForCalendar(service) && !hasCalendarEvent(service),
      )
      .map((service) => service.id);

    if (eligibleIds.length === 0) {
      const ineligibleCount = selectedServices.filter(
        (s) => !isServiceEligibleForCalendar(s),
      ).length;
      const alreadyHaveEvents = selectedServices.filter(
        (s) => isServiceEligibleForCalendar(s) && hasCalendarEvent(s),
      ).length;

      let reason = "";
      if (ineligibleCount > 0 && alreadyHaveEvents > 0) {
        reason = `${ineligibleCount} are not interpretations and ${alreadyHaveEvents} already have calendar events.`;
      } else if (ineligibleCount > 0) {
        reason = `${ineligibleCount} of the selected assignments are not eligible interpretations.`;
      } else if (alreadyHaveEvents > 0) {
        reason = `All ${alreadyHaveEvents} selected assignments already have calendar events.`;
      }

      showSnackbar(`No eligible assignments to add. ${reason}`, theme, "info");
      return;
    }

    const message =
      eligibleIds.length === selectedIds.length
        ? `Add ${eligibleIds.length} assignment(s) to calendar?`
        : `Only ${eligibleIds.length} out of ${selectedIds.length} selected assignments are eligible (must be Interpretations without calendar events). Add these to calendar?`;

    openDialog({
      theme,
      title: "Add Assignments to Calendar",
      message,
      secondaryAction: "Cancel",
      primaryAction: "Add to Calendar",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          setCalendarSyncProgress({
            isOpen: true,
            current: 0,
            total: eligibleIds.length,
          });
          closeDialog();

          let successCount = 0;
          let failureCount = 0;

          // Process each service individually to track progress
          for (let i = 0; i < eligibleIds.length; i++) {
            const serviceId = eligibleIds[i];
            try {
              await api.post(`/calendar/sync/${serviceId}`);
              successCount++;
            } catch (error) {
              failureCount++;
            }
            // Update progress
            setCalendarSyncProgress({
              isOpen: true,
              current: i + 1,
              total: eligibleIds.length,
            });
          }

          // Close progress overlay
          setCalendarSyncProgress({ isOpen: false, current: 0, total: 0 });

          // Show result summary
          if (failureCount === 0) {
            showSnackbar(
              `${successCount} assignment(s) added to calendar`,
              theme,
              "success",
            );
          } else {
            showSnackbar(
              `${successCount} added, ${failureCount} failed`,
              theme,
              failureCount > 0 ? "warning" : "success",
            );
          }

          handleCancelSelection();
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          setCalendarSyncProgress({ isOpen: false, current: 0, total: 0 });
          const message =
            error.response?.data?.message ||
            "Failed to add assignments to calendar.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        }
      },
    });
  };

  // Handle date selection save (combined months and years)
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Take only the last selected year
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Save to localStorage
    localStorage.setItem(
      CONST.INTERPRETATION_MONTHS_KEY,
      JSON.stringify(updatedSelection.months),
    );
    localStorage.setItem(
      CONST.INTERPRETATION_YEAR_KEY,
      JSON.stringify(updatedSelection.years),
    );

    // Fetch data
    fetchInterpretationsData(updatedSelection.months, updatedSelection.years);
  };

  const fetchInterpretationsData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);

      // Ensure we have valid parameters
      let finalMonths = months;
      let finalYear = year;

      if (!finalMonths || finalMonths.length === 0) {
        finalMonths = [dayjs().format("MMM")];
        // Update both states to reflect the default value
        setSelectedMonths(finalMonths);
        setTempSelectedMonths(finalMonths);
      }

      if (!finalYear || finalYear.length === 0) {
        finalYear = [String(currentYear)];
        // Update both states to reflect the default value
        setSelectedYear(finalYear);
        setTempSelectedYear(finalYear);
      }

      // Convert months to a comma-separated string if it's an array
      const monthsParam = Array.isArray(finalMonths)
        ? finalMonths.join(",")
        : finalMonths;

      api
        .get("/services/interpretations", {
          params: {
            months: monthsParam,
            year: finalYear[0], // Since year is single-select, we only need the first (and only) value
          },
        })
        .then((response) => {
          if (response !== null) {
            //REVERSE LISTS
            const services = response.data;
            services.reverse();
            setListOfServices(services);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";
          handleErrorDialog(message, theme);

          setIsLoading(false);
        });
    },
    [currentYear, selectedMonths, selectedYear, handleErrorDialog, theme],
  );

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/clients/OPIMinimums"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
        api.get("/dashboard/serviceDateRange"),
        api.get("/services/translations/deliveryTypes"),
        api.get("/services/translations/paymentTypes"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            clientsOPIMinimumsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            minMaxDatesResponse,
            deliveryTypesResponse,
            paymentTypesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              const tempLanguages = typesResponse.data.slice(0, 5);
              setListOfTypes(tempLanguages);
            }
            if (clientsOPIMinimumsResponse) {
              setListOfClientsOPIMinimums(clientsOPIMinimumsResponse.data);
            }
            if (minMaxDatesResponse) {
              setMinMaxDates(minMaxDatesResponse.data);
            }
            if (deliveryTypesResponse) {
              const filteredDeliveryTypes = deliveryTypesResponse.data.filter(
                (type) => !["CODA", "SmartCat", "In-person"].includes(type),
              );
              setDeliveryTypes(filteredDeliveryTypes);
            }
            if (paymentTypesResponse) {
              setPaymentTypes(paymentTypesResponse.data);
            }
            // Mark data lists as ready
            setIsDataListsReady(true);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsDataListsReady(true); // Even on error, mark as ready to prevent infinite waiting
      });
  }, [handleErrorDialog, theme]);

  fetchInterpretationsDataRef.current = fetchInterpretationsData;
  getDataListsRef.current = getDataLists;

  useEffect(() => {
    fetchInterpretationsDataRef.current();
    getDataListsRef.current();
    hasInitializedDataRef.current = true;
  }, []);

  useEffect(() => {
    if (!hasInitializedDataRef.current) {
      return;
    }

    fetchInterpretationsDataRef.current();
  }, [refreshPage]);

  // Listen for interpretation updates from other members
  const handleInterpretationsListRefetch = useCallback(
    (updatedBy) => {
      // Reload the interpretations list data
      fetchInterpretationsData();
    },
    [fetchInterpretationsData],
  );

  useAssignmentUpdates({
    modelType: "InterpretationsList",
    recordId: "all",
    onUpdate: handleInterpretationsListRefetch,
  });

  // Handle pending create new action when data lists are ready
  useEffect(() => {
    if (pendingCreateNew && isDataListsReady) {
      const data = {
        Clients: listOfClients,
        ClientsOPIMinimums: listOfClientsOPIMinimums,
        Linguists: listOfLinguists,
        Languages: listOfLanguages,
        Interpretations: listOfServices,
        InterpretationStatuses: listOfStatuses,
        InterpretationTypes: listOfTypes,

        Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
      };

      setOverlayData(data);
      setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
      setPendingCreateNew(false);
    }
  }, [
    pendingCreateNew,
    isDataListsReady,
    listOfClients,
    listOfClientsOPIMinimums,
    listOfLinguists,
    listOfLanguages,
    listOfServices,
    listOfStatuses,
    listOfTypes,
    setOverlayData,
    setOverlayName,
  ]);

  useEffect(() => {
    if (pendingPreAssign && isDataListsReady) {
      const data = {
        Clients: listOfClients,
        ClientsOPIMinimums: listOfClientsOPIMinimums,
        Linguists: listOfLinguists,
        Languages: listOfLanguages,
        Interpretations: listOfServices,
        InterpretationStatuses: listOfStatuses,
        InterpretationTypes: listOfTypes,
      };

      setOverlayData(data);
      setOverlayName(OVERLAY_NAMES.INTERPRETATION_PRE_ASSIGN);
      setPendingPreAssign(false);
    }
  }, [
    pendingPreAssign,
    isDataListsReady,
    listOfClients,
    listOfClientsOPIMinimums,
    listOfLinguists,
    listOfLanguages,
    listOfServices,
    listOfStatuses,
    listOfTypes,
    setOverlayData,
    setOverlayName,
  ]);

  // Sync the temporary state with the actual state when the actual state changes from outside
  useEffect(() => {
    setTempSelectedMonths(selectedMonths);
  }, [selectedMonths]);

  useEffect(() => {
    setTempSelectedYear(selectedYear);
  }, [selectedYear]);

  const timeComparator = (v1, v2) => {
    const format = "h:mm A"; // Matches "hh:MM a" like "1:30 PM"
    const t1 = dayjs(v1, format);
    const t2 = dayjs(v2, format);

    return t1.isBefore(t2) ? -1 : t1.isAfter(t2) ? 1 : 0;
  };

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  const columns = [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 110,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      flex: 1.1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value =
          data && data !== "" ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 100,
      flex: 1.0,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: getDateProximityColor(
                theme,
                params.value,
                params.row.InterpretationStatus,
              ),
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[1] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data && data.Account ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TimeStart",
      sortComparator: timeComparator,
      headerName: "Time Start",
      valueGetter: (_, params) => {
        const data = params.TimeStart;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TimeEnd",
      sortComparator: timeComparator,
      headerName: "Time End",
      valueGetter: (_, params) => {
        const data = params.TimeEnd;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Duration",
      headerName: "Duration",
      valueGetter: (_, params) => {
        const data = params.Duration;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 115,
      flex: 1.15,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "InterpretationStatus",
      headerName: "Status",
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <HtmlTooltip
            title={
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {params.value}
              </Typography>
            }
          >
            <Box
              sx={{
                borderRadius: "8px",
                px: "0.5rem",
                backgroundColor: getStatusColor(theme, params.value),
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: theme.palette.background.default,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          </HtmlTooltip>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "InterpretationType",
      headerName: "Interpretation Type",
      valueGetter: (_, params) => {
        const data = params.ServiceSubtype?.Name;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      minWidth: 185,
      flex: 1.85,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Address",
      headerName: "Address",
      valueGetter: (_, params) => {
        const data = params.Address;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 220,
      flex: 2.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TargetLanguage",
      headerName: "Language",
      valueGetter: (_, params) => {
        const languages = params.ServiceTargetLanguages;
        if (!languages || languages.length === 0) return "N/A";
        // Extract the Language.Name from each item and filter out any empty values
        return languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AdditionalCost",
      headerName: "Additional Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "TotalLinguistPay",
      headerName: "Total Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalLinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistPay",
      headerName: "Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.LinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AdditionalLinguistCost",
      headerName: "Additional Linguist Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalLinguistCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 220,
      flex: 2.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "EquipmentCost",
      headerName: "Equipment Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.EquipmentCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },


    {
      field: "MileageRoundtrip",
      headerName: "Mileage Roundtrip",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.MileageRoundtrip;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `${data}`;
        }

        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "MileageCost",
      headerName: "Mileage Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.MileageCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceClientRateSimultaneous",
      headerName: "Client Simultaneous Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.ServiceClientRateSimultaneous;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 230,
      flex: 2.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Rate",
      headerName: "Linguist Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Rate;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "RateSimultaneous",
      headerName: "Linguist Simultaneous Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.RateSimultaneous;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 250,
      flex: 2.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Profit",
      headerName: "Profit",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Profit;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Margin",
      headerName: "Margin",
      valueGetter: (_, params) => {
        const data = params.Margin;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const percentage = data * 1;
          if (percentage === 0) {
            value = "0%";
          } else if (percentage === 100) {
            value = "100%";
          } else {
            value = `${percentage.toFixed(2)}%`;
          }
        }

        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "PreparationTime",
      headerName: "Preparation Time",
      valueGetter: (_, params) => {
        const data = params.PreparationTime;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 175,
      flex: 1.75,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "SchedulingTime",
      headerName: "Scheduling Time",
      valueGetter: (_, params) => {
        const data = params.SchedulingTime;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    
    {
      field: "ActualStart",
      sortComparator: timeComparator,
      headerName: "Actual Start",
      valueGetter: (_, params) => {
        const data = params.ActualStart;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ActualEnd",
      sortComparator: timeComparator,
      headerName: "Actual End",
      valueGetter: (_, params) => {
        const data = params.ActualEnd;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalDuration",
      headerName: "Total Duration",
      valueGetter: (_, params) => {
        const data = params.TotalDuration;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Cancelled",
      headerName: "Cancelled",
      valueGetter: (_, params) => {
        const data = params.Cancelled;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Cancelled;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CancellationReason",
      headerName: "Cancellation Reason",
      valueGetter: (_, params) => {
        const data = params.CancellationReason;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      minWidth: 200,
      flex: 2.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Invoiced",
      headerName: "Invoiced",
      valueGetter: (_, params) => {
        const data = params.Invoiced;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Invoiced;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CreatedBy",
      headerName: "Created By",
      valueGetter: (_, params) => {
        const data = params.Creator;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedBy",
      headerName: "Checked By",
      valueGetter: (_, params) => {
        const data = params.Checker;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedOn",
      headerName: "Checked On",
      valueGetter: (_, params) => {
        const data = params.CheckingDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";

        return value;
      },
      sortComparator: dateComparator,
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const [filterModel, setFilterModel] = useState(getStoredFilterModel);
  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);
  const [sortModel, setSortModel] = useState(getStoredSortModel);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfServices || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listOfServices || listOfServices.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listOfServices, loadAllColumnFilters]); // Apply saved filters once per loaded dataset

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
    localStorage.setItem(
      CONST.INTERPRETATION_FILTER_KEY,
      JSON.stringify({ items: [] }),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) =>
          row.ProjectNumber && row.ProjectNumber !== ""
            ? row.ProjectNumber
            : "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date && row.Date !== ""
            ? dayjs.tz(row.Date).format("M/D/YYYY")
            : "N/A",
      },
      {
        field: "Client",
        valueGetter: (row) =>
          row.Client && row.Client?.Account ? row.Client?.Account : "N/A",
      },
      {
        field: "Requestor",
        valueGetter: (row) => (row.Requestor ? row.Requestor.Name : "N/A"),
      },
      {
        field: "TimeStart",
        valueGetter: (row) => {
          const data = row.TimeStart;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "TimeEnd",
        valueGetter: (row) => {
          const data = row.TimeEnd;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "Duration",
        valueGetter: (row) => {
          const data = row.Duration;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "InterpretationStatus",
        valueGetter: (row) => row.InterpretationStatus || "N/A",
      },
      {
        field: "AccountCharge",
        valueGetter: (row) => {
          const data = row.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      {
        field: "TotalCost",
        valueGetter: (row) => {
          const data = row.TotalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "LinguistPay",
        valueGetter: (row) => {
          const data = row.LinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "TotalLinguistPay",
        valueGetter: (row) => {
          const data = row.TotalLinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "AdditionalCost",
        valueGetter: (row) => {
          const data = row.AdditionalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "ServiceClientRateSimultaneous",
        valueGetter: (row) => {
          const data = row.ServiceClientRateSimultaneous;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "RateSimultaneous",
        valueGetter: (row) => {
          const data = row.RateSimultaneous;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "EquipmentCost",
        valueGetter: (row) => {
          const data = row.EquipmentCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Linguist",
        valueGetter: (row) => (row.Linguist ? row.Linguist.Name : "N/A"),
      },
      {
        field: "TargetLanguage",
        valueGetter: (row) => {
          const languages = row.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          return languages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");
        },
      },
      {
        field: "Profit",
        valueGetter: (row) => {
          const data = row.Profit;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Margin",
        valueGetter: (row) => {
          const data = row.Margin;
          if (data !== undefined && data !== null) {
            const percentage = data * 1;
            if (percentage === 0) return "0%";
            if (percentage === 100) return "100%";
            return `${percentage.toFixed(2)}%`;
          }
          return "N/A";
        },
      },
      {
        field: "InterpretationType",
        valueGetter: (row) => row.ServiceSubtype?.Name || "N/A",
      },
      { field: "Address", valueGetter: (row) => row.Address || "N/A" },
      {
        field: "MileageRoundtrip",
        valueGetter: (row) => {
          const data = row.MileageRoundtrip;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "MileageCost",
        valueGetter: (row) => {
          const data = row.MileageCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "ParkingFeesTolls",
        valueGetter: (row) => {
          const data = row.ParkingFeesTolls;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "PreparationTime",
        valueGetter: (row) => {
          const data = row.PreparationTime;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      {
        field: "SchedulingTime",
        valueGetter: (row) => {
          const data = row.SchedulingTime;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      {
        field: "ActualStart",
        valueGetter: (row) => {
          const data = row.ActualStart;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "ActualEnd",
        valueGetter: (row) => {
          const data = row.ActualEnd;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "TotalDuration",
        valueGetter: (row) => {
          const data = row.TotalDuration;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      {
        field: "Rate",
        valueGetter: (row) => {
          const data = row.Rate;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Cancelled",
        valueGetter: (row) => {
          const data = row.Cancelled;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CancellationReason",
        valueGetter: (row) =>
          row.CancellationReason && row.CancellationReason !== ""
            ? row.CancellationReason
            : "N/A",
      },
      {
        field: "Invoiced",
        valueGetter: (row) => {
          const data = row.Invoiced;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CreatedBy",
        valueGetter: (row) => (row.Creator ? row.Creator.Name : "N/A"),
      },
      {
        field: "CheckedBy",
        valueGetter: (row) => (row.Checker ? row.Checker.Name : "N/A"),
      },
      {
        field: "CheckedOn",
        valueGetter: (row) =>
          row.CheckingDate
            ? dayjs.tz(row.CheckingDate).format("M/D/YYYY")
            : "N/A",
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listOfServices || [];
      }

      return (listOfServices || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listOfServices, filterColumns]);

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      CONST.INTERPRETATION_FILTER_KEY,
      JSON.stringify(newFilterModel),
    );
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(
      CONST.INTERPRETATION_SORT_KEY,
      JSON.stringify(newSortModel),
    );
  };

  let totalAccountCharge = 0;
  let totalTotalCost = 0;
  let totalProfit = 0;

  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));
  const includeAll = filteredRowIdsSet.size === 0;

  // Use columnFilteredData for totals calculation to include column filter effects
  const dataForTotals = columnFilteredData || [];

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    totalAccountCharge += Number(service.AccountCharge);
    totalTotalCost += Number(service.TotalCost);
    totalProfit += Number(service.Profit);
  });

  const listOfTotals = [
    { name: "Total Account Charge", total: totalAccountCharge },
    { name: "Total Total Cost", total: totalTotalCost },
    { name: "Total Profit", total: totalProfit },
  ];

  // Count statuses for Members
  let completedCount = 0;
  let confirmedCount = 0;
  let requestedCount = 0;
  let cancelledCount = 0;

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    const status = service.InterpretationStatus;
    if (status === "Completed") completedCount++;
    else if (status === "Confirmed") confirmedCount++;
    else if (status === "Requested") requestedCount++;
    else if (service.Cancelled) cancelledCount++;
  });

  const listOfServiceStatuses = [
    { name: "Completed", count: completedCount },
    { name: "Confirmed", count: confirmedCount },
    { name: "Requested", count: requestedCount },
    { name: "Cancelled", count: cancelledCount },
  ];

  // State for toggling between totals and statuses
  // Check localStorage first, then fall back to role-based default (Admin: totals, Member: statuses)
  const getInitialShowTotals = () => {
    const saved = localStorage.getItem(CONST.INTERPRETATIONS_SHOW_TOTALS_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return currentUser?.role !== "Admin";
  };

  const [showTotals, setShowTotals] = useState(getInitialShowTotals);

  // Save to localStorage when toggling
  const handleToggleShowTotals = () => {
    const newValue = !showTotals;
    setShowTotals(newValue);
    localStorage.setItem(
      CONST.INTERPRETATIONS_SHOW_TOTALS_KEY,
      JSON.stringify(newValue),
    );
  };

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={!columns.some((col) => columnsVisible[col.field] === false)}
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services
              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              const csvRows = dataToExport
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            // Check if this field has a special mapping
                            const mappedPath = fieldPaths[col.field];
                            if (mappedPath) {
                              value = getFieldValue(row, mappedPath);
                            } else if (col.field === "TargetLanguage") {
                              // Special handling for TargetLanguage (complex nested array)
                              const languages = row.ServiceTargetLanguages;
                              if (languages && Array.isArray(languages)) {
                                value = languages
                                  .map((tl) => tl.Language?.Name || "")
                                  .join(" | ");
                              }
                            } else {
                              value = getFieldValue(row, col.field);
                              if (value === "" && col.field in row) {
                                value = row[col.field];
                              }
                            }
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `interpretations-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              // Create workbook and worksheet
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Interpretations");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              dataToExport.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      const mappedPath = fieldPaths[col.field];
                      if (mappedPath) {
                        value = getFieldValue(row, mappedPath);
                      } else if (col.field === "TargetLanguage") {
                        const languages = row.ServiceTargetLanguages;
                        if (languages && Array.isArray(languages)) {
                          value = languages
                            .map((tl) => tl.Language?.Name || "")
                            .join(" | ");
                        }
                      } else {
                        value = getFieldValue(row, col.field);
                        if (value === "" && col.field in row) {
                          value = row[col.field];
                        }
                      }
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });


              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `interpretations-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
      selectionEnabled: toolbarSelectionEnabled,
      selectionModel: toolbarSelectionModel,
      allSelectedChecked: toolbarAllSelectedChecked,
      onCancelSelection,
      onMarkChecked,
      onAddToCalendar,
    }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const buttons = [];

      if (toolbarSelectionEnabled) {
        buttons.push({
          label: "Cancel Selection",
          onClick: () => {
            onCancelSelection();
            handleClose();
          },
          icon: null,
        });
        buttons.push({
          label: "Mark As Checked",
          onClick: () => {
            onMarkChecked();
            handleClose();
          },
          icon: <TaskAltRoundedIcon />,
          disabled: toolbarSelectionModel.length === 0,
          isChecked: toolbarAllSelectedChecked,
        });
        // Only show Add To Calendar for admins
        if (currentUser?.role === "Admin") {
          buttons.push({
            label: "Add To Calendar",
            onClick: () => {
              onAddToCalendar();
              handleClose();
            },
            icon: <NotificationsActiveRoundedIcon />,
            disabled: toolbarSelectionModel.length === 0,
          });
        }
      }

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobile && toolbarSelectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TaskAltRoundedIcon />}
                disabled={toolbarSelectionModel.length === 0}
                onClick={onMarkChecked}
                sx={{
                  mr: 1,
                  color: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.text.disabled
                    : toolbarTheme.palette.primary.main,
                  borderColor: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.action.disabled
                    : toolbarTheme.palette.primary.main,
                  "&:hover": toolbarAllSelectedChecked
                    ? {
                        borderColor: toolbarTheme.palette.action.disabled,
                        backgroundColor: "transparent",
                        color: toolbarTheme.palette.text.disabled,
                      }
                    : {
                        borderColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        backgroundColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        color: toolbarTheme.palette.background.default,
                      },
                }}
              >
                Mark As Checked
              </Button>
              {currentUser?.Role === "Admin" && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<NotificationsActiveRoundedIcon />}
                  disabled={toolbarSelectionModel.length === 0}
                  onClick={onAddToCalendar}
                  sx={{
                    mr: 1,
                    color: toolbarTheme.palette.primary.main,
                    borderColor: toolbarTheme.palette.primary.main,
                    "&:hover": {
                      borderColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      color: toolbarTheme.palette.background.default,
                    },
                  }}
                >
                  Add To Calendar
                </Button>
              )}
            </>
          )}

          {isMobile && buttons.length > 0 && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  {buttons.map((btn, index) => (
                    <MenuItem
                      key={index}
                      onClick={btn.onClick}
                      disabled={btn.disabled}
                      sx={{
                        color: btn.isChecked
                          ? theme.palette.text.disabled
                          : theme.palette.primary.main,
                      }}
                    >
                      {btn.icon && (
                        <ListItemIcon
                          sx={{
                            color: btn.isChecked
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                          }}
                        >
                          {btn.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText>{btn.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Box>
              </Popover>
            </>
          )}

          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.alt,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobile && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <CEQuickFilter iconOnly={isMobile} />

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: toolbarTheme.palette.grey.main,
                  borderColor: toolbarTheme.palette.divider,
                  border: `1px solid ${toolbarTheme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: toolbarTheme.palette.primary.main,
                    //backgroundColor: toolbarTheme.palette.primary.buttonHoverSelected,
                    color: toolbarTheme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openDialog({
                    title: "Export Data",
                    theme: theme,
                    customContent: <ExportDialogContent />,
                  });
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme, isMobile, openDialog, currentUser?.Role, currentUser?.role]);

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        <Box
          sx={{
            p: "1rem",
            display: "flex",
            flexWrap: "wrap", // wrap to new line if too long
            gap: 2, // spacing between items
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={handleToggleShowTotals}
            size="small"
            sx={{
              color: theme.palette.primary.main,
              "&:hover": {
                color: theme.palette.primary.buttonHoverSelected,
              },
            }}
          >
            <SwapHorizRoundedIcon fontSize="small" />
          </IconButton>

          {showTotals
            ? listOfTotals.map((total) => {
                return (
                  <Box key={`${total}-${total.name}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap", justifyContent: "center" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {total.name}:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme?.palette?.secondary?.[400],
                          fontWeight: 600,
                        }}
                      >
                        ${Number(total.total).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })
            : listOfServiceStatuses.map((status) => {
                return (
                  <Box key={`${status}-${status.name}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap", justifyContent: "center" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status.name}:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme?.palette?.secondary?.[400],
                          fontWeight: 600,
                        }}
                      >
                        {status.count}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
        </Box>

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  return (
    <Box
      //borderRadius="1.5rem 1.5rem 0 0"
      p="1rem 1rem 0 1rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Adjust to take full viewport height minus margin
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Calendar Sync Progress Overlay */}
      {calendarSyncProgress.isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            backgroundColor: theme.palette.background.paper,
            borderRadius: "8px",
            p: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            minWidth: "300px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Adding to Calendar
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: theme.palette.text.secondary }}
          >
            {calendarSyncProgress.current} / {calendarSyncProgress.total}
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: "8px",
              backgroundColor: theme.palette.action.disabled,
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                backgroundColor: theme.palette.primary.main,
                width: `${(calendarSyncProgress.current / calendarSyncProgress.total) * 100}%`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Overlay Backdrop */}
      {calendarSyncProgress.isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 9998,
          }}
        />
      )}

      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid size={{ xs: 12 }} sx={{ px: 1 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  flex: isMobile ? 0 : 1,
                  maxWidth: isMobile ? undefined : FILTER_SELECTOR_WIDTH,
                }}
              >
                <CustomDateSelector
                  label="Date Range"
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={tempSelectedMonths}
                  selectedYears={tempSelectedYear}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={false}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>

              <Box display="flex" gap={2} alignItems="center">
                {!isMobile ? (
                  <>
                    <Button
                      onClick={() => {
                        setOverlayName(OVERLAY_NAMES.INTERPRETATION_REMINDERS);
                        setOverlayData({});
                      }}
                      startIcon={<NotificationsActiveRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>Reminders</Box>
                    </Button>

                    <Button
                      onClick={() => {
                        setPendingPreAssign(true);
                      }}
                      startIcon={<PersonSearchRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        Pre-Assign Outreach
                      </Box>
                    </Button>

                    <Button
                      onClick={() => {
                        setPendingCreateNew(true);
                      }}
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.background.default,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          color: theme.palette.background.default,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        Add Interpretation
                      </Box>
                    </Button>
                  </>
                ) : (
                  <>
                    <Tooltip title="Reminders">
                      <IconButton
                        onClick={() => {
                          setOverlayName(
                            OVERLAY_NAMES.INTERPRETATION_REMINDERS,
                          );
                          setOverlayData({});
                        }}
                        sx={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: theme.palette.background.default,
                          boxShadow:
                            "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                          borderRadius: "12px",
                          border: "none",
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.buttonHover,
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        <NotificationsActiveRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      onClick={() => {
                        setPendingPreAssign(true);
                      }}
                      sx={{
                        width: "40px",
                        minWidth: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <PersonSearchRoundedIcon />
                    </Button>

                    <Button
                      onClick={() => {
                        setPendingCreateNew(true);
                      }}
                      sx={{
                        width: "40px",
                        minWidth: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.background.default,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          color: theme.palette.background.default,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <AddRoundedIcon />
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGrid
          localeText={{
            noRowsLabel: "No interpretation projects added yet for this month.",
            noResultsOverlayLabel: "No matches for your filters",
          }}
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          disableColumnSorting
          onStateChange={(e) => {
            const lookup = e.filter?.filteredRowsLookup;
            if (!lookup) return;

            const nextIds = Object.keys(lookup).filter((key) => lookup[key]);
            nextIds.sort();

            const prevIds = prevFilteredRowIdsRef.current;
            const isSame =
              prevIds.length === nextIds.length &&
              prevIds.every((id, idx) => id === nextIds[idx]);

            if (!isSame) {
              prevFilteredRowIdsRef.current = nextIds;
              setFilterRowIds(nextIds);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          apiRef={apiRef}
          density={density}
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            if (selectionEnabled) {
              if (suppressNextClickRef.current) {
                event.defaultMuiPrevented = true;
                suppressNextClickRef.current = false;
              }
              return;
            }

            setSelectionModel([params.id]);
          }}
          onRowDoubleClick={(params) => {
            if (openFilter || selectionEnabled) return;

            const data = {
              Interpretation: params.row,
              InterpretationID: params.row.id,

              Clients: listOfClients,
              Linguists: listOfLinguists,
              Languages: listOfLanguages,
              Interpretations: listOfServices,
              InterpretationStatuses: listOfStatuses,
              InterpretationTypes: listOfTypes,

              ComingFrom: `/interpretations`,
            };

            navigate(`/interpretations/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "No" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar, footer: customFooter }}
          columns={filteredColumns}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: () => clearAllFiltersRef.current(),
              theme,
              selectionEnabled,
              selectionModel,
              allSelectedChecked: selectionStats.allChecked,
              onCancelSelection: handleCancelSelection,
              onMarkChecked: handleMarkSelectedAsChecked,
              onAddToCalendar: handleAddSelectedToCalendar,
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            height: "100%", // Make DataGrid take full height of container
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />

        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Edit
            </ListItemText>
          </MenuItem>

          {canCompleteSelected && (
            <MenuItem onClick={handleContextMenuComplete}>
              <ListItemIcon>
                <TaskRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.primary.text }}>
                Complete
              </ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={handleContextMenuDuplicate}>
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Duplicate
            </ListItemText>
          </MenuItem>

          <MenuItem onClick={handleContextMenuAddTask}>
            <ListItemIcon>
              <AddTaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Add Task
            </ListItemText>
          </MenuItem>

          <MenuItem onClick={handleContextMenuAddNote}>
            <ListItemIcon>
              <CommentRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Add Note
            </ListItemText>
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          <MenuItem onClick={handleContextMenuCallRequestor}>
            <ListItemIcon>
              <CallRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Call{" "}
              {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
                ?.Name || "Requestor"}
            </ListItemText>
          </MenuItem>

          <MenuItem onClick={handleContextMenuEmailRequestor}>
            <ListItemIcon>
              <EmailRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Email{" "}
              {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
                ?.Name || "Requestor"}
            </ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={{
            left: window.innerWidth / 2,
            top: window.innerHeight / 2,
          }}
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              borderRadius: "12px",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.15)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Interpretations;
